import React, { useMemo } from 'react';
import { Card, Row, Col, Statistic, Space, Typography } from 'antd';
import { getAddOnConfig, type AddOnType, calculatePriceWithTax } from '@shared/config/subscription-addons';

const { Text } = Typography;

type SubscriptionAddOnListItem = {
  type?: AddOnType;
  quantity?: number;
  totalPrice?: number;
  startDate?: string | Date;
  unitPrice?: number;
  endDate?: string | Date | null;
};

interface AddonSummaryCardProps {
  addOns?: SubscriptionAddOnListItem[];
}

export const AddonSummaryCard: React.FC<AddonSummaryCardProps> = ({ addOns }) => {
  const totalMonthlyPrice = useMemo(() => {
    if (!addOns || addOns.length === 0) return 0;
    return addOns.reduce((sum, addon) => sum + (addon.totalPrice ?? 0), 0);
  }, [addOns]);

  const totalMonthlyPriceWithTax = useMemo(() => {
    return calculatePriceWithTax(totalMonthlyPrice);
  }, [totalMonthlyPrice]);

  const addonCountByType = useMemo(() => {
    if (!addOns || addOns.length === 0) return {};
    return addOns.reduce((acc, addon) => {
      if (!addon.type) {
        return acc;
      }
      acc[addon.type] = (acc[addon.type] || 0) + 1;
      return acc;
    }, {} as Record<AddOnType, number>);
  }, [addOns]);

  const hasAddons = addOns && addOns.length > 0;

  return (
    <Card>
      <Row gutter={16}>
        <Col span={8}>
          <Statistic
            title="追加オプション数"
            value={addOns?.length || 0}
            suffix="件"
          />
        </Col>
        <Col span={8}>
          <Space direction="vertical" size="small">
            <Text type="secondary" style={{ fontSize: 14 }}>月額合計</Text>
            <div>
              <Text strong style={{ fontSize: 24 }}>
                ¥{totalMonthlyPrice.toLocaleString()}/月
              </Text>
            </div>
            <div>
              <Text type="secondary" style={{ color: '#999', fontSize: 14 }}>
                (税込{totalMonthlyPriceWithTax.toLocaleString()}円/月)
              </Text>
            </div>
          </Space>
        </Col>
        <Col span={8}>
          <Space direction="vertical" size="small">
            <Text type="secondary">内訳:</Text>
            {hasAddons ? (
              Object.entries(addonCountByType as Record<AddOnType, number>).map(([type, count]) => (
                <Text key={type}>
                  {getAddOnConfig(type as AddOnType).label}: {count}件
                </Text>
              ))
            ) : (
              <Text type="secondary">追加オプションなし</Text>
            )}
          </Space>
        </Col>
      </Row>
    </Card>
  );
};

export default AddonSummaryCard;
